import cc from 'currency-codes';
import getSymbolFromCurrency from 'currency-symbol-map';

import type { CardTransaction } from '../api/client.ts';

// Shared presentation logic for the transaction list, used by both the
// dashboard preview and the full activity screen so the two cannot drift.

// The card backend reports some currencies as the ISO 4217 numeric code
// (e.g. '826' for GBP) rather than the alpha code, so the raw value must be
// normalised before any symbol lookup or comparison.
const toAlphaCode = (code: string): string => {
  if (code.length === 3 && /^[A-Z]+$/.test(code)) return code;
  return cc.number(code)?.code ?? code;
};

// Resolves a currency code to its glyph across ~170 currencies. Accepts alpha
// or numeric ISO codes. When a code has no glyph the code itself is shown with
// a trailing space so it reads as a prefix; absent currency defaults to the
// dollar glyph, matching the rest of the app's USD-first assumption.
export const currencySymbol = (code: string | undefined): string => {
  if (code === undefined) return '$';
  const normalized = code.trim().toUpperCase();
  if (normalized === '') return '$';
  const alpha = toAlphaCode(normalized);
  return getSymbolFromCurrency(alpha) ?? `${alpha} `;
};

export interface AmountParts {
  readonly int: string;
  readonly dec: string;
}

// Splits a numeric magnitude into a grouped integer part and a 2dp fraction.
// Always operates on the absolute value — callers render the sign separately.
export const formatMagnitude = (n: number): AmountParts => {
  const abs = Math.abs(n);
  const int = Math.floor(abs).toLocaleString('en-US');
  const decPart = abs.toFixed(2).split('.')[1];
  return { int, dec: `.${decPart ?? '00'}` };
};

// Direction comes from `type`, never from the sign of `amount`: the card
// backend reports spends as positive magnitudes, so a positive value is not a
// credit. Top-ups are the only credit; card transactions and platform-fee
// commissions are debits. A genuine refund (a reversed card transaction) is
// indistinguishable from a spend on this contract and therefore renders as a
// debit — an accepted limitation until the backend exposes an explicit
// direction field.
export const isCredit = (tx: CardTransaction): boolean => tx.type === 'TOP_UP';

// The unified shape carries the merchant descriptor in `details`. `description`
// is only a fallback for any older payload; the placeholder keeps an empty row
// from looking broken.
export const readMerchant = (tx: CardTransaction): string => {
  const details = tx.details?.trim();
  if (details !== undefined && details !== '') return details;
  const description = tx.description?.trim();
  if (description !== undefined && description !== '') return description;
  return 'Transaction';
};

export interface Money {
  readonly amount: number;
  readonly currency: string | undefined;
}

// Statuses worth naming on the row itself. Every one of these either has not
// settled yet or settled at zero, so the amount beside it does not mean "this
// much left your card" — a refused purchase that reads like a completed one is
// exactly the confusion this list has to avoid. Anything else (a plain
// completed or cleared transaction) needs no word.
const STATUS_NOTES: Readonly<Record<string, string>> = {
  PENDING: 'Pending',
  DECLINED: 'Declined',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  CANCELED: 'Cancelled',
  REVERSED: 'Reversed',
};

export const statusNote = (tx: CardTransaction): string | null =>
  STATUS_NOTES[tx.status.trim().toUpperCase()] ?? null;

// The same statuses, as a set for the money-movement math. Derived from the
// label list rather than written out a second time: a row captioned as not
// settled must never also be counted as settled money, and two hand-kept lists
// would eventually disagree about which rows those are.
const UNSETTLED_STATUSES: ReadonlySet<string> = new Set(Object.keys(STATUS_NOTES));

// A row whose figures describe an ATTEMPT rather than a movement: a declined
// authorization settles at zero, so the backend reports what was presented in
// the attempted fields instead. Such a row must still be shown — that is the
// point of the attempted amount — but it must be kept out of every total, or a
// day's spend would include purchases that were refused. The status is checked
// as well as the attempted fields because a refused row can arrive with neither
// attempted leg, and reading it as settled would net its zero — or worse, its
// raw amount — into the day's spend.
export const movedMoney = (tx: CardTransaction): boolean =>
  !UNSETTLED_STATUSES.has(tx.status.trim().toUpperCase()) &&
  tx.attemptedAmount === undefined &&
  tx.attemptedMerchantAmount === undefined;

// An unsettled row whose own amount is zero and which carries no attempted leg
// either — the backend cannot always report one — has no figure to show at
// all. Rendering its "0" as "−$0.00" is the exact reading this display exists
// to prevent: a purchase that never happened must not look like a free one.
// Keyed off the same status set as `movedMoney` rather than a second list, so
// the two cannot disagree about which rows those are.
const hasNoBillingFigure = (tx: CardTransaction): boolean =>
  UNSETTLED_STATUSES.has(tx.status.trim().toUpperCase()) &&
  tx.attemptedAmount === undefined &&
  tx.attemptedMerchantAmount === undefined &&
  (Number(tx.amount) || 0) === 0;

// The main figure on a row is always in the card's own currency — that is a
// product fact, not a formatting choice — so this reads the card leg and only
// the card leg. A declined attempt substitutes the amount that was presented
// for the settled zero, but never a different currency: see `readBilling`
// returning null below.
export const readBilling = (tx: CardTransaction): Money | null => {
  if (tx.attemptedAmount !== undefined) {
    return { amount: Math.abs(Number(tx.attemptedAmount) || 0), currency: tx.currency };
  }
  if (tx.attemptedMerchantAmount !== undefined) {
    // A declined foreign-currency authorization is never priced, so no figure
    // in the card's currency exists. Null, not zero: the row has nothing
    // honest to say in dollars and must not imply otherwise. The amount that
    // WAS presented is still reported, on the merchant line (see `readFx`).
    return null;
  }
  // Unsettled and the provider reported no amount at all: null for the same
  // reason — there is no figure here, and "0" is not one.
  if (hasNoBillingFigure(tx)) return null;
  return { amount: Math.abs(Number(tx.amount) || 0), currency: tx.currency };
};

// The original-currency leg of a foreign-currency purchase, shown as a
// sub-amount. Suppressed when it would just repeat the billing currency.
export const readFx = (tx: CardTransaction): { readonly amount: number; readonly currency: string } | null => {
  // On an attempted row the settled merchant amount is zero too, so the sub-leg
  // has to come from the attempt as well.
  const merchantAmount = tx.attemptedMerchantAmount ?? tx.merchantAmount;
  if (merchantAmount === undefined || tx.merchantCurrency === undefined) return null;
  // Compare in alpha space: the billing and merchant legs can disagree on alpha
  // vs numeric encoding for the same currency, which would otherwise show a
  // bogus sub-amount for a same-currency purchase.
  const fxAlpha = toAlphaCode(tx.merchantCurrency.trim().toUpperCase());
  const billAlpha = toAlphaCode((tx.currency ?? '').trim().toUpperCase());
  if (fxAlpha === billAlpha) return null;
  return { amount: Math.abs(Number(merchantAmount) || 0), currency: fxAlpha };
};

// The provider's own words for why a transaction was refused (insufficient
// balance, blocked merchant, ...) — the part that tells the cardholder what to
// do next.
export const declineNote = (tx: CardTransaction): string | null => {
  const reason = tx.declineReason?.trim();
  return reason !== undefined && reason !== '' ? reason : null;
};

export const txTypeLabel = (tx: CardTransaction, credit: boolean): string => {
  if (credit) return 'Crypto deposit';
  if (tx.type === 'COMMISSION') return 'Platform fee';
  return 'Card transaction';
};

// Date-group total: credits add, debits subtract, magnitudes only — so the
// netted figure matches the per-row signs the list shows. Attempted rows are
// skipped: their displayed figure is money that never left the card, and
// netting it here would report spend that did not happen. (It would also be
// nonsense arithmetic when the attempt is quoted in the merchant's currency.)
export const signedTotal = (txs: readonly CardTransaction[]): number =>
  txs
    .filter(movedMoney)
    .reduce((sum, tx) => sum + (isCredit(tx) ? 1 : -1) * (readBilling(tx)?.amount ?? 0), 0);
